class FlixSearchModal extends HTMLElement {
  connectedCallback() {
    this._input = this.querySelector('[data-flix-search-input]');
    this._results = this.querySelector('[data-flix-search-results]');
    this._backdrop = this.querySelector('[data-flix-search-backdrop]');
    this._closeBtn = this.querySelector('[data-flix-search-close]');
    this._limit = parseInt(this.dataset.resultLimit, 10) || 5;
    this._lastQuery = '';
    this._timer = null;
    this._teardown = new AbortController();
    const signal = this._teardown.signal;

    document.addEventListener('flix:search:open', () => this.open(), { signal });
    this._backdrop.addEventListener('click', () => this.close());
    this._closeBtn.addEventListener('click', () => this.close());
    // On window, in capture: a third-party script that stops Escape on
    // document (the support widget does, while its teaser shows) must not
    // be able to keep this dialog open.
    window.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !this.hidden) this.close();
    }, { capture: true, signal });
    this._input.addEventListener('input', () => {
      clearTimeout(this._timer);
      this._timer = setTimeout(() => this._search(this._input.value.trim()), 220);
    });
    this.querySelector('.flx-search-modal__form').addEventListener('submit', (e) => {
      const q = this._input.value.trim();
      if (!q) e.preventDefault();
    });
  }

  // The Theme Editor swaps this section's markup in place. Without this
  // the old element's document listeners keep answering for it.
  disconnectedCallback() {
    clearTimeout(this._timer);
    if (this._teardown) this._teardown.abort();
    // Removed while open: give back the scroll lock and the modal count it
    // was holding, or the page stays frozen behind a modal that is gone.
    if (!this.hidden) this.close();
  }

  open() {
    this.hidden = false;
    requestAnimationFrame(() => this._input.focus());
    document.body.style.overflow = 'hidden';
    window.flixModalOpen();
  }

  close() {
    this.hidden = true;
    document.body.style.overflow = '';
    window.flixModalClose();
  }

  async _search(query) {
    this._lastQuery = query;
    if (!query) { this._results.innerHTML = ''; return; }
    this._results.innerHTML = '<p class="flx-search-modal__empty">' + this.dataset.i18nLoading + '</p>';
    const url = this.dataset.predictiveUrl +
      '?q=' + encodeURIComponent(query) +
      '&resources[type]=product,collection,page' +
      '&resources[limit]=' + this._limit +
      '&resources[options][unavailable_products]=last' +
      '&section_id=search-modal';
    try {
      let results = null;
      if (!this._predictiveUnsupported) {
        const res = await fetch(url, { headers: { Accept: 'application/json' } });
        // Shopify answers 417 for a language its predictive search does not
        // support (Arabic); from then on the store's own search is asked.
        if (res.status === 417) this._predictiveUnsupported = true;
        else if (!res.ok) throw new Error('search failed');
        else results = (await res.json()).resources;
      }
      if (this._predictiveUnsupported) results = await this._storeSearch(query);
      if (query !== this._lastQuery) return;
      this._render(results && results.results, query);
    } catch (e) {
      if (query !== this._lastQuery) return;
      this._results.innerHTML = '';
    }
  }

  // The store's own search, through templates/search.modal.liquid, which
  // answers in the same shape as predictive search.
  async _storeSearch(query) {
    const url = this.dataset.searchUrl +
      '?q=' + encodeURIComponent(query) +
      '&type=product,page&options[prefix]=last&view=modal';
    const res = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error('search failed');
    return JSON.parse(await res.text()).resources;
  }

  _render(results, query) {
    if (!results) { this._results.innerHTML = ''; return; }
    const products = (results.products || []).slice(0, this._limit);
    const collections = results.collections || [];
    const pages = results.pages || [];
    if (!products.length && !collections.length && !pages.length) {
      this._results.innerHTML = '<p class="flx-search-modal__empty">' +
        this.dataset.i18nNoResults.replace('%%QUERY%%', () => this._escape(query)) + '</p>';
      return;
    }
    let html = '';
    if (products.length) {
      html += '<div class="flx-search-modal__group-title">' + this.dataset.i18nProducts + '</div>';
      products.forEach((p) => { html += this._itemHtml(p.url, p.image, p.title, p.price); });
    }
    if (collections.length) {
      html += '<div class="flx-search-modal__group-title">' + this.dataset.i18nCollections + '</div>';
      collections.forEach((c) => { html += this._itemHtml(c.url, c.image, c.title, ''); });
    }
    if (pages.length) {
      html += '<div class="flx-search-modal__group-title">' + this.dataset.i18nPages + '</div>';
      pages.forEach((p) => { html += this._itemHtml(p.url, null, p.title, ''); });
    }
    html += '<a class="flx-search-modal__view-all" href="' + this.dataset.searchUrl + '?q=' + encodeURIComponent(query) + '">' +
      this.dataset.i18nViewAll + '</a>';
    this._results.innerHTML = html;
  }

  _itemHtml(url, image, title, price) {
    // Predictive-search JSON is server-controlled catalog data, but every
    // interpolated value is still attribute-/HTML-escaped as defense in depth
    // so a crafted product title/URL can never break out of its context.
    const img = image ? '<img src="' + this._attr(image) + '" alt="" loading="lazy" width="44" height="44">' : '';
    const priceHtml = price ? '<span class="flx-search-modal__item-price">' + this._escape(price) + '</span>' : '';
    return '<a class="flx-search-modal__item" href="' + this._attr(url) + '">' + img +
      '<span class="flx-search-modal__item-title">' + this._escape(title) + '</span>' + priceHtml + '</a>';
  }

  // Escapes text content (&, <, >). Safe for text between tags.
  _escape(s) {
    const d = document.createElement('div');
    d.textContent = s || '';
    return d.innerHTML;
  }

  // Escapes a double-quoted attribute value (adds " and ' on top of _escape).
  _attr(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}
customElements.define('flix-search-modal', FlixSearchModal);
